import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("kts_token");
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("kts_token");
      localStorage.removeItem("kts_user");
      window.location.reload();
    }
    return Promise.reject(error);
  },
);

export function calcNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  const diff = new Date(checkOut) - new Date(checkIn);
  return Math.max(Math.ceil(diff / 86400000), 0);
}

function r2(n) {
  return Math.round((parseFloat(n) || 0) * 100) / 100;
}

export function calcGST(base, taxRate, saleState, purchaseState, mode) {
  const rate      = parseFloat(taxRate) || 0;
  const taxAmount = r2((base * rate) / 100);
  const sameState = saleState === purchaseState;
  if (mode === "Full Input" || sameState) {
    return { cgst: r2(taxAmount / 2), sgst: r2(taxAmount / 2), igst: 0, total: taxAmount, type: "CGST+SGST" };
  }
  return { cgst: 0, sgst: 0, igst: taxAmount, total: taxAmount, type: "IGST" };
}

// ── Aggregate room_type_bookings into sub-totals ──────────────────────────────
function aggregateRoomTypeBookings(rtBookings, nights, saleState, purchaseState) {
  const n         = parseFloat(nights) || 1;
  const sameState = saleState === purchaseState;

  let subTotalSale     = 0;
  let subTotalPurchase = 0;
  let totalGSTSale     = 0;
  let totalGSTPurchase = 0;
  let totalTACSale     = 0;
  let totalTACPurchase = 0;
  let extrasSale       = 0;
  let extrasPurchase   = 0;
  let totalRoomsPurchaseForPLB = 0;
  let totalRooms       = 0;

  for (const b of rtBookings) {
    const qty = parseInt(b.count) || 0;

    // ── Room charges ─────────────────────────────────────────────────────────
    // Sub Total (Purchase) is the GROSS rate — TAC is NOT baked in here.
    // TAC is a flat, single deduction applied once when Total Purchase is
    // computed (Sub + GST − TAC), and once more, separately, in Gross Profit.
    const tacRoom       = parseFloat(b.tac_on_room) || 0;
    const roomSaleBase  = r2(qty * n * (parseFloat(b.sale_price) || 0));
    const roomPurBase   = r2(qty * n * (parseFloat(b.purchase_price) || 0));
    const roomSaleGst   = r2(roomSaleBase * ((parseFloat(b.gst_rate)          || 0) / 100));
    const roomPurGst    = r2(roomPurBase  * ((parseFloat(b.purchase_gst_rate) || 0) / 100));

    subTotalSale     += roomSaleBase;
    subTotalPurchase += roomPurBase;
    totalGSTSale     += roomSaleGst;
    totalGSTPurchase += roomPurGst;
    totalRoomsPurchaseForPLB += roomPurBase;
    totalRooms       += qty;

    // ── Extra bed — Child (5–12 yrs) — no TAC on child, unchanged ────────────
    const ebcCount = parseInt(b.extra_bed_child) || 0;
    const ebcSale  = r2(ebcCount * n * (parseFloat(b.ex_bed_rate_child_sale)     || 0));
    const ebcPur   = r2(ebcCount * n * (parseFloat(b.ex_bed_rate_child_purchase) || 0));
    const ebcGst   = r2(ebcSale * ((parseFloat(b.extra_bed_child_gst) || 0) / 100));
    const ebcPGst  = r2(ebcPur  * ((parseFloat(b.extra_bed_child_gst) || 0) / 100));

    subTotalSale     += ebcSale;
    subTotalPurchase += ebcPur;
    totalGSTSale     += ebcGst;
    totalGSTPurchase += ebcPGst;

    // ── Extra bed — Adult (12 yrs+) — gross rate, TAC applied once, later ──
    const ebaCount   = parseInt(b.extra_bed_adult) || 0;
    const tacExBed    = parseFloat(b.tac_on_ex_bed) || 0;
    const ebaSale    = r2(ebaCount * n * (parseFloat(b.ex_bed_rate_sale)     || 0));
    const ebaPur     = r2(ebaCount * n * (parseFloat(b.ex_bed_rate_purchase) || 0));
    const ebaGst     = r2(ebaSale * ((parseFloat(b.extra_bed_adult_gst) || 0) / 100));
    const ebaPGst    = r2(ebaPur  * ((parseFloat(b.extra_bed_adult_gst) || 0) / 100));

    subTotalSale     += ebaSale;
    subTotalPurchase += ebaPur;
    totalGSTSale     += ebaGst;
    totalGSTPurchase += ebaPGst;

    // ── TAC — a per-night rate, multiplied by nights (not room count) ────────
    totalTACSale     += n * (r2(parseFloat(b.room_tac_passed) || 0) + r2(parseFloat(b.ex_bed_tac_passed) || 0));
    totalTACPurchase += n * (r2(parseFloat(b.tac_on_room)     || 0) + r2(parseFloat(b.tac_on_ex_bed)     || 0));

    // ── Extras ───────────────────────────────────────────────────────────────
    extrasSale     += parseFloat(b.extras_amount_sale)     || 0;
    extrasPurchase += parseFloat(b.extras_amount_purchase) || 0;
  }

  return {
    subTotalSale:     r2(subTotalSale),
    subTotalPurchase: r2(subTotalPurchase),
    totalGSTSale:     r2(totalGSTSale),
    totalGSTPurchase: r2(totalGSTPurchase),
    totalTACSale:     r2(totalTACSale),
    totalTACPurchase: r2(totalTACPurchase),
    extrasSale:       r2(extrasSale),
    extrasPurchase:   r2(extrasPurchase),
    totalRoomsPurchaseForPLB: r2(totalRoomsPurchaseForPLB),
    totalRooms,
  };
}

// ── Master financial compute ──────────────────────────────────────────────────
export function computeFinancials(data) {
  const {
    // Legacy fields (used when room_type_bookings absent)
    single_rooms = 0, double_rooms = 0,
    extra_bed = 0, extra_bed_child = 0, extra_bed_adult = 0,
    total_nights = 0, no_of_nights = 0,
    rate_single = 0, rate_double = 0,
    ex_bed_rate_sale = 0, ex_bed_rate_child_sale = 0,
    rate_single_purchase = 0, rate_double_purchase = 0,
    ex_bed_rate_purchase = 0, ex_bed_rate_child_purchase = 0,
    tax_rate_sale = 0, tax_rate_purchase = 0,
    room_tac_passed = 0, ex_bed_tac_passed = 0,
    tac_on_room = 0, tac_on_ex_bed = 0,
    extras_amount_sale = 0, extras_amount_purchase = 0,
    mode = "Full Input",
    billing_state_sale = "", billing_state_purchase = "",
    plb_applicable = "No", plb_type, plb_nights = 0, plb_percent = 0,
    // New room_type_bookings array
    room_type_bookings,
  } = data;

  const n          = parseFloat(total_nights) || parseFloat(no_of_nights) || 1;
  const useNewPath = Array.isArray(room_type_bookings) && room_type_bookings.length > 0;

  let subTotalSale, subTotalPurchase;
  let totalTACSale = 0, totalTACPurchase = 0;
  let extrasSale = 0, extrasPurchase = 0;
  let totalRoomsPurchaseForPLB = 0, totalRooms = 0;
  let aggregatedGSTSale     = null;
  let aggregatedGSTPurchase = null;

  if (useNewPath) {
    // ── NEW: per-room-type aggregation ────────────────────────────────────────
    const agg = aggregateRoomTypeBookings(room_type_bookings, n, billing_state_sale, billing_state_purchase);
    subTotalSale              = agg.subTotalSale;
    subTotalPurchase          = agg.subTotalPurchase;
    aggregatedGSTSale         = agg.totalGSTSale;
    aggregatedGSTPurchase     = agg.totalGSTPurchase;
    totalTACSale              = agg.totalTACSale;
    totalTACPurchase          = agg.totalTACPurchase;
    extrasSale                = agg.extrasSale;
    extrasPurchase            = agg.extrasPurchase;
    totalRoomsPurchaseForPLB  = agg.totalRoomsPurchaseForPLB;
    totalRooms                = agg.totalRooms;
  } else {
    // ── LEGACY: single/double rates ───────────────────────────────────────────
    const sr  = parseFloat(single_rooms)    || 0;
    const dr  = parseFloat(double_rooms)    || 0;
    const ebc = parseFloat(extra_bed_child) || 0;
    const eba = parseFloat(extra_bed_adult) || parseFloat(extra_bed) || 0;

    subTotalSale = r2(
      sr  * parseFloat(rate_single) * n +
      dr  * parseFloat(rate_double) * n +
      ebc * parseFloat(ex_bed_rate_child_sale) * n +
      eba * parseFloat(ex_bed_rate_sale) * n
    );
    subTotalPurchase = r2(
      sr  * parseFloat(rate_single_purchase) * n +
      dr  * parseFloat(rate_double_purchase) * n +
      ebc * parseFloat(ex_bed_rate_child_purchase) * n +
      eba * parseFloat(ex_bed_rate_purchase) * n
    );
    totalTACSale     = r2(n * (parseFloat(room_tac_passed) + parseFloat(ex_bed_tac_passed)));
    totalTACPurchase = r2(n * (parseFloat(tac_on_room)     + parseFloat(tac_on_ex_bed)));
    extrasSale       = parseFloat(extras_amount_sale)     || 0;
    extrasPurchase   = parseFloat(extras_amount_purchase) || 0;
    totalRooms       = (sr + dr);
    totalRoomsPurchaseForPLB = subTotalPurchase;
  }

  // ── Service Charge ────────────────────────────────────────────────────────────
  const serviceCharge    = r2(subTotalSale - subTotalPurchase);
  const isNegativeSC     = mode === "Service Charge" && serviceCharge < 0;
  const absServiceCharge = Math.abs(serviceCharge);

  // ── GST on Sale ───────────────────────────────────────────────────────────────
  // GST is computed the same way regardless of mode — each side independently,
  // using its own per-room aggregated rate.
  let gstSale, taxBaseSale;
  const sameState = billing_state_sale === billing_state_purchase;

  if (useNewPath) {
    taxBaseSale = subTotalSale;
    gstSale = {
      cgst:  sameState ? r2(aggregatedGSTSale / 2) : 0,
      sgst:  sameState ? r2(aggregatedGSTSale / 2) : 0,
      igst:  sameState ? 0 : aggregatedGSTSale,
      total: aggregatedGSTSale,
      type:  sameState ? "CGST+SGST" : "IGST",
    };
  } else {
    taxBaseSale = subTotalSale;
    gstSale = calcGST(taxBaseSale, tax_rate_sale, billing_state_sale, billing_state_purchase, mode);
  }

  // ── GST on Purchase ───────────────────────────────────────────────────────────
  let gstPurchase;
  if (useNewPath) {
    gstPurchase = {
      cgst:  sameState ? r2(aggregatedGSTPurchase / 2) : 0,
      sgst:  sameState ? r2(aggregatedGSTPurchase / 2) : 0,
      igst:  sameState ? 0 : aggregatedGSTPurchase,
      total: aggregatedGSTPurchase,
      type:  sameState ? "CGST+SGST" : "IGST",
    };
  } else {
    gstPurchase = calcGST(subTotalPurchase, tax_rate_purchase, billing_state_sale, billing_state_purchase, mode);
  }

  // ── Total Sale / Purchase ─────────────────────────────────────────────────────
  let totalSale, autoCalculatedSale = null;
  if (mode === "Service Charge") {
    if (isNegativeSC) {
      // Negative SC: total = |SC| + GST on |SC|
      autoCalculatedSale = r2(absServiceCharge + gstSale.total);
      totalSale          = autoCalculatedSale;
    } else {
      // Positive SC: total = full sale + GST on SC only (not on full sale)
      totalSale = r2(subTotalSale + gstSale.total + extrasSale);
    }
  } else {
    totalSale = r2(subTotalSale + gstSale.total + extrasSale);
  }
  const totalPurchase = r2(Math.max(0, subTotalPurchase + gstPurchase.total - totalTACPurchase) + extrasPurchase);

  // ── PLB ───────────────────────────────────────────────────────────────────────
  let plbAmount = 0;
  if (plb_applicable === "Yes") {
    if (plb_type === "Night") {
      const avgPurRate = totalRooms > 0 ? totalRoomsPurchaseForPLB / (totalRooms * n) : 0;
      plbAmount = r2(parseFloat(plb_nights) * avgPurRate);
    } else if (plb_type === "Percent") {
      plbAmount = r2((totalTACPurchase * parseFloat(plb_percent)) / 100);
    }
  }

  // ── Gross Profit ────────────────────────────────────────────────────────────
  // Service Charge: GST-inclusive Total Sale − Total Purchase (Total Purchase
  // already nets TAC on the purchase side; TAC on the sale side is ignored).
  // Full Input: two rules, in priority order:
  // 1. Total Purchase < ₹7,500 → Basic Sale − Total Purchase.
  // 2. Otherwise → (Basic Sale − TAC on Sale) − (Basic Purchase − TAC on
  //    Purchase). GST is ignored entirely on both sides here.
  // PLB is netted off the result either way.
  const grossProfitInclGst = r2(totalSale - totalPurchase - plbAmount);
  let grossProfit;
  if (mode === "Service Charge") {
    grossProfit = grossProfitInclGst;
  } else if (totalPurchase < 7500) {
    grossProfit = r2(subTotalSale - totalPurchase - plbAmount);
  } else {
    const netSale     = subTotalSale     - totalTACSale;
    const netPurchase = subTotalPurchase - totalTACPurchase;
    grossProfit = r2(netSale - netPurchase - plbAmount);
  }

  return {
    sub_total_sale:              isNegativeSC ? r2(absServiceCharge) : subTotalSale,
    original_sub_total_sale:     subTotalSale,
    tax_amount_sale:             gstSale.total,
    total_tac_sale:              totalTACSale,
    total_sale:                  totalSale,
    gst_sale:                    gstSale,
    tax_base_sale:               taxBaseSale,
    sub_total_purchase:          subTotalPurchase,
    tax_amount_purchase:         gstPurchase.total,
    total_tac_purchase:          totalTACPurchase,
    total_purchase:              totalPurchase,
    gst_purchase:                gstPurchase,
    service_charge:              serviceCharge,
    abs_service_charge:          absServiceCharge,
    is_negative_service_charge:  isNegativeSC,
    auto_calculated_sale:        autoCalculatedSale,
    is_negative_profit:          grossProfit < 0,
    plb_amount:                  plbAmount,
    gross_profit:                grossProfit,
  };
}

export const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export const SALE_TYPES       = ["Hotel", "Ticket", "Event", "Commission", "Package"];
export const MODES            = ["Full Input", "Service Charge"];
export const PLB_TYPES        = ["Night", "Percent"];
export const BOOKING_STATUSES = ["Draft", "Pending Approval", "Approved", "Invoiced"];